import React from 'react';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from './index.module.scss';
import SubjectProfileMiniWidgetMultiValue, {
    SubjectProfileMiniWidgetMultiValueProps,
} from './subject-profile-mini-widget-multi-value';
import SubjectProfileMiniWidgetSingleValue, {
    SubjectProfileMiniWidgetSingleValueProps,
} from './subject-profile-mini-widget-single-value';
import { SubjectDetailsModel } from '@interfaces-clean/subject-monitoring.interface';
import { useTranslation } from '@hooks/index';
import { getStudy_OpraQueries_study_Study } from '@queries/generated/studies.generated';
// eslint-disable-next-line max-len
import { subjectProfileDetailsConfiguration_OpraQueries_subjectProfileDetailsConfiguration_SubjectProfileDetailsConfiguration_subjectProfileDetailsConfigurationItem_SubjectProfileDetailsConfigurationItem_subjectStatus_SubjectProfileDetailsConfigurationItemSubjectStatus as SubjectProfileSubjectStatusConfig } from '@queries/generated/subject-monitoring.generated';

type SingleValueMiniWidgetOptions = Omit<SubjectProfileMiniWidgetSingleValueProps, 'data-testid'> & {
    key: string;
    visible: boolean;
};

type MultiValueMiniWidgetOptions = Omit<SubjectProfileMiniWidgetMultiValueProps, 'data-testid'> & {
    key: string;
    visible: boolean;
};

type Props = {
    config: SubjectProfileSubjectStatusConfig;
    subjectDetails: SubjectDetailsModel;
    study: getStudy_OpraQueries_study_Study | null | undefined;
};

const SubjectProfileDetailsMiniWidgets = ({ config, subjectDetails, study }: Props) => {
    const { t } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.miniWidgets',
    });

    // Determine which single-value mini widgets are visible
    const singleValueMiniWidgets: SingleValueMiniWidgetOptions[] = [
        {
            visible: config?.numberOfAEs === true,
            key: 'subject-profile-AEs-mini-widget',
            icon: <FontAwesomeIcon icon={['fas', 'truck-medical']} className={styles.miniWidgetIcon} />,
            label: t('NumberOfAEs'),
            value: subjectDetails?.aEsAll,
        },
        {
            visible: config?.numberOfSAEs === true,
            key: 'subject-profile-SAEs-mini-widget',
            icon: <FontAwesomeIcon icon={['fas', 'truck-medical']} className={styles.miniWidgetIcon} />,
            label: t('NumberOfSAEs'),
            value: subjectDetails?.saEsAll,
        },
        {
            visible: config?.numberOfAESIs === true,
            key: 'subject-profile-AESIs-mini-widget',
            icon: (
                <FontAwesomeIcon
                    icon={['fak', 'fa-solid-user-injured-circle-exclamation' as IconName]}
                    className={styles.miniWidgetIcon}
                />
            ),
            label: t('NumberOfAESIs'),
            value: subjectDetails?.aEsSpecialInterest,
        },
    ].filter((w) => w.visible);

    // Determine which multi-value mini widgets are visible and contain at least 1 visible item
    const multiValueMiniWidgets: MultiValueMiniWidgetOptions[] = [
        {
            visible: config?.combiVisits === true,
            key: 'subject-profile-visits-mini-widget',
            icon: (
                <FontAwesomeIcon
                    icon={['fad', 'house-medical-flag']}
                    className={cn(styles.miniWidgetIcon, styles.colorDuotone)}
                />
            ),
            label: t('combiVisits.visits'),
            items: [
                {
                    visible: true,
                    key: 'visitsExpCompletedAll',
                    icon: <FontAwesomeIcon icon={['fas', 'circle']} className={styles.miniWidgetMultiValueIcon} />,
                    label: t('combiVisits.completed'),
                    value: subjectDetails?.visitsExpCompletedAll,
                },
                {
                    visible: study?.calculateVisits === true,
                    key: 'visitsExpCompletedOowAll',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'square']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecondary)}
                        />
                    ),
                    label: t('combiVisits.outOfWindow'),
                    value: subjectDetails?.visitsExpCompletedOowAll,
                },
                {
                    visible: study?.calculateVisits === true,
                    key: 'visitsUnscheduledCompleted',
                    icon: <FontAwesomeIcon icon={['fas', 'diamond']} className={styles.miniWidgetMultiValueIcon} />,
                    label: t('combiVisits.unscheduled'),
                    value: subjectDetails?.visitsUnscheduledCompleted,
                },
                {
                    visible: study?.calculateVisits === true,
                    key: 'visitsExpMissing',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'triangle']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecondary)}
                        />
                    ),
                    label: t('combiVisits.missing'),
                    value: subjectDetails?.visitsExpMissing,
                },
                {
                    visible: true,
                    key: 'visitsExpNotDone',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'triangle']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecondary, styles.rotate180)}
                        />
                    ),
                    label: t('combiVisits.notDone'),
                    value: subjectDetails?.visitsExpNotDone,
                },
            ],
        },
        {
            visible: config?.combiAssessments === true,
            key: 'subject-profile-assessments-mini-widget',
            icon: <FontAwesomeIcon icon={['fas', 'file']} className={styles.miniWidgetIcon} />,
            label: t('combiAssessments.assessments'),
            items: [
                {
                    visible: true,
                    key: 'assessExpAllCompleted',
                    icon: <FontAwesomeIcon icon={['fas', 'circle']} className={styles.miniWidgetMultiValueIcon} />,
                    label: t('combiAssessments.completed'),
                    value: subjectDetails?.assessExpAllCompleted,
                },
                {
                    visible: study?.calculateAssessments === true,
                    key: 'assessExpCompletedOow',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'square']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecondary)}
                        />
                    ),
                    label: t('combiAssessments.outOfWindow'),
                    value: subjectDetails?.assessExpCompletedOow,
                },
                {
                    visible: study?.calculateAssessments === true,
                    key: 'assessExpAllMissing',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'triangle']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecon)}
                        />
                    ),
                    label: t('combiAssessments.missing'),
                    value: subjectDetails?.assessExpAllMissing,
                },
                {
                    visible: true,
                    key: 'AssessExpAllNotDone',
                    icon: (
                        <FontAwesomeIcon
                            icon={['fas', 'triangle']}
                            className={cn(styles.miniWidgetMultiValueIcon, styles.colorSecondary, styles.rotate180)}
                        />
                    ),
                    label: t('combiAssessments.notDone'),
                    value: subjectDetails?.assessExpAllNotDone,
                },
            ],
        },
        {
            visible: config?.numberOfQueriesOpenAll === true,
            key: 'subject-profile-queries-mini-widget',
            icon: <FontAwesomeIcon icon={['fas', 'message-question']} className={styles.miniWidgetIcon} />,
            label: t('NumberOfQueries'),
            items: [
                {
                    visible: true,
                    key: 'numberOfQueries',
                    icon: <FontAwesomeIcon icon={['fas', 'circle']} className={styles.miniWidgetMultiValueIcon} />,
                    label: t('NumberOfQueries'),
                    value: subjectDetails?.queriesOpenAll,
                },
            ],
        },
        {
            visible: config?.numberOfPDs === true,
            key: 'subject-profile-pds-mini-widget',
            icon: <FontAwesomeIcon icon={['fas', 'octagon-exclamation']} className={styles.miniWidgetIcon} />,
            label: t('NumberOfPDs'),
            items: [
                {
                    visible: true,
                    key: 'numberOfPDs',
                    icon: <FontAwesomeIcon icon={['fas', 'circle']} className={styles.miniWidgetMultiValueIcon} />,
                    label: t('NumberOfPDs'),
                    value: subjectDetails?.pDsAll,
                },
            ],
        },
    ].filter((w) => w.visible && w.items.some((i) => i.visible));

    if (singleValueMiniWidgets.length === 0 && multiValueMiniWidgets.length === 0) {
        return null;
    }

    return (
        <div className={styles.miniWidgets}>
            {singleValueMiniWidgets.length > 0 && (
                <div className={styles.singleValueMiniWidgetList}>
                    {singleValueMiniWidgets.map((x) => (
                        <SubjectProfileMiniWidgetSingleValue
                            key={x.key}
                            data-testid={x.key}
                            label={x.label}
                            value={x.value}
                        />
                    ))}
                </div>
            )}

            {multiValueMiniWidgets.length > 0 && (
                <div className={styles.multiValueMiniWidgetList}>
                    {multiValueMiniWidgets.map((x) => (
                        <SubjectProfileMiniWidgetMultiValue
                            key={x.key}
                            data-testid={x.key}
                            icon={x.icon}
                            label={x.label}
                            items={x.items}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SubjectProfileDetailsMiniWidgets;
